// Stripe Connect utilities for allowing users to connect their Stripe accounts

import { saveSettings, loadSettings } from './firebaseSettings';

/**
 * Generate the Stripe Connect OAuth URL
 * This redirects users to Stripe to authorize your platform
 */
export const getStripeConnectUrl = (userEmail) => {
  const clientId = process.env.REACT_APP_STRIPE_CONNECT_CLIENT_ID;

  if (!clientId) {
    throw new Error('Stripe Connect Client ID is not configured. Check your .env file.');
  }

  // Use email as state to track which user is connecting
  const state = btoa(JSON.stringify({ email: userEmail, timestamp: Date.now() }));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    state: state,
    scope: 'read_only',
    redirect_uri: `${window.location.origin}/stripe-connect/callback`,
    'stripe_user[email]': userEmail,
  });

  return `https://connect.stripe.com/oauth/authorize?${params.toString()}`;
};

/**
 * Handle the OAuth callback from Stripe
 * Exchange the authorization code for connected account details
 */
export const handleStripeConnectCallback = async (code, state) => {
  try {
    // Decode state to get user email
    const { email } = JSON.parse(atob(state));

    // Exchange authorization code for access token
    const response = await fetch('https://connect.stripe.com/oauth/token', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Failed to connect Stripe account');
    }

    const data = await response.json();

    // Save the connected account information to Firebase
    await saveConnectedAccount(email, {
      stripeUserId: data.stripe_user_id,
      accessToken: data.access_token, // Store securely - consider encryption
      refreshToken: data.refresh_token,
      scope: data.scope,
      tokenType: data.token_type,
      connectedAt: Date.now(),
    });

    return {
      success: true,
      accountId: data.stripe_user_id,
    };
  } catch (error) {
    console.error('Error handling Stripe Connect callback:', error);
    throw error;
  }
};

/**
 * Save connected account information to Firebase
 */
export const saveConnectedAccount = async (userEmail, accountData) => {
  try {
    const settings = await loadSettings(userEmail);

    const updatedSettings = {
      ...settings,
      stripeConnectedAccount: accountData,
    };

    await saveSettings(userEmail, updatedSettings);
    console.log('✅ Stripe connected account saved to Firebase');
  } catch (error) {
    console.error('Error saving connected account:', error);
    throw error;
  }
};

/**
 * Get connected account information from Firebase
 */
export const getConnectedAccount = async (userEmail) => {
  try {
    const settings = await loadSettings(userEmail);
    return settings.stripeConnectedAccount || null;
  } catch (error) {
    console.error('Error getting connected account:', error);
    return null;
  }
};

/**
 * Check if user has a connected Stripe account
 */
export const hasConnectedAccount = async (userEmail) => {
  const account = await getConnectedAccount(userEmail);
  return !!account?.stripeUserId;
};

/**
 * Disconnect Stripe account
 */
export const disconnectStripeAccount = async (userEmail) => {
  try {
    const account = await getConnectedAccount(userEmail);

    if (!account?.stripeUserId) {
      throw new Error('No connected account found');
    }

    // Revoke access to the connected account
    const response = await fetch('https://connect.stripe.com/oauth/deauthorize', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: process.env.REACT_APP_STRIPE_CONNECT_CLIENT_ID,
        stripe_user_id: account.stripeUserId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to disconnect Stripe account');
    }

    // Remove from Firebase
    const settings = await loadSettings(userEmail);
    delete settings.stripeConnectedAccount;
    await saveSettings(userEmail, settings);

    console.log('✅ Stripe account disconnected');
    return { success: true };
  } catch (error) {
    console.error('Error disconnecting Stripe account:', error);
    throw error;
  }
};

/**
 * Get account balance for connected account
 */
export const getAccountBalance = async (userEmail) => {
  try {
    const account = await getConnectedAccount(userEmail);

    if (!account?.stripeUserId) {
      throw new Error('No connected account found');
    }

    const response = await fetch(`https://api.stripe.com/v1/balance`, {
      headers: {
        'Authorization': `Bearer ${account.accessToken}`,
        'Stripe-Account': account.stripeUserId,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch account balance');
    }

    const balance = await response.json();
    return balance;
  } catch (error) {
    console.error('Error fetching account balance:', error);
    throw error;
  }
};

/**
 * Check if a customer email has an active subscription in the connected account
 * This is used to check if incoming email senders are paying customers
 */
export const checkCustomerSubscription = async (userEmail, customerEmail) => {
  try {
    const account = await getConnectedAccount(userEmail);

    if (!account?.stripeUserId) {
      console.log('No connected Stripe account found');
      return { hasSubscription: false, subscriptions: [] };
    }

    // Search for customer by email in the connected account
    const response = await fetch(
      `https://api.stripe.com/v1/customers?email=${encodeURIComponent(customerEmail)}&limit=1`,
      {
        headers: {
          'Authorization': `Bearer ${account.accessToken}`,
          'Stripe-Account': account.stripeUserId,
        },
      }
    );

    if (!response.ok) {
      console.error('Failed to fetch customer from connected account');
      return { hasSubscription: false, subscriptions: [] };
    }

    const data = await response.json();

    if (data.data.length === 0) {
      // Customer not found in this Stripe account
      return { hasSubscription: false, subscriptions: [] };
    }

    const customer = data.data[0];

    // Fetch customer's subscriptions
    const subsResponse = await fetch(
      `https://api.stripe.com/v1/subscriptions?customer=${customer.id}&status=active&limit=10`,
      {
        headers: {
          'Authorization': `Bearer ${account.accessToken}`,
          'Stripe-Account': account.stripeUserId,
        },
      }
    );

    if (!subsResponse.ok) {
      console.error('Failed to fetch subscriptions from connected account');
      return { hasSubscription: false, subscriptions: [] };
    }

    const subsData = await subsResponse.json();

    const activeSubscriptions = subsData.data.filter(
      sub => sub.status === 'active' || sub.status === 'trialing'
    );

    return {
      hasSubscription: activeSubscriptions.length > 0,
      subscriptions: activeSubscriptions.map(sub => ({
        id: sub.id,
        status: sub.status,
        plan: sub.items.data[0]?.price?.nickname || 'Subscription',
        amount: sub.items.data[0]?.price?.unit_amount / 100,
        currency: sub.items.data[0]?.price?.currency?.toUpperCase() || 'USD',
        currentPeriodEnd: sub.current_period_end * 1000,
        cancelAtPeriodEnd: sub.cancel_at_period_end,
      })),
      customer: {
        id: customer.id,
        name: customer.name,
        email: customer.email,
      },
    };
  } catch (error) {
    console.error('Error checking customer subscription:', error);
    return { hasSubscription: false, subscriptions: [] };
  }
};
